import { MarkerWithPopup } from '@streetferret/map-web';
import { Button } from '@streetferret/ui-web/button';
import {
  CardDescription,
  CardHeader,
  CardTitle,
} from '@streetferret/ui-web/card';
import { ExternalLink, MapPinned } from 'lucide-react';

function googleMapsDirectionsUrl(lat: number, lng: number) {
  return `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
}

export type JourneyWaypointMarker = {
  waypointId: number;
  seqNo: number;
  title: string;
  description: string | null;
  lat: number;
  lng: number;
};

export interface JourneyWaypointMarkersProps {
  waypoints: JourneyWaypointMarker[];
}

export function JourneyWaypointMarkers({
  waypoints,
}: JourneyWaypointMarkersProps) {
  return (
    <>
      {waypoints.map(waypoint => (
        <JourneyWaypointMarker key={waypoint.waypointId} waypoint={waypoint} />
      ))}
    </>
  );
}

interface JourneyWaypointMarkerProps {
  waypoint: JourneyWaypointMarker;
}

function JourneyWaypointMarker({ waypoint }: JourneyWaypointMarkerProps) {
  const { lat, lng, title, description, seqNo } = waypoint;

  return (
    <MarkerWithPopup
      lat={lat}
      lng={lng}
      marker={
        <div
          className="relative flex size-7 cursor-pointer items-center justify-center rounded-full border-2 border-background bg-primary text-primary-foreground shadow-md"
          title={title}>
          <span className="text-xs font-semibold leading-none">{seqNo}</span>
        </div>
      }
      popup={
        <CardHeader className="max-w-xs space-y-1">
          <div className="flex items-start gap-2">
            <MapPinned className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
            <div className="min-w-0 space-y-1">
              <CardTitle className="text-base">{title}</CardTitle>
              {description ? (
                <CardDescription className="whitespace-pre-wrap">
                  {description}
                </CardDescription>
              ) : null}
              <Button
                variant="link"
                size="sm"
                className="h-auto p-0 text-blue-600 hover:text-blue-800 hover:underline dark:text-blue-400 dark:hover:text-blue-300"
                asChild>
                <a
                  href={googleMapsDirectionsUrl(lat, lng)}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={e => e.stopPropagation()}>
                  Directions
                  <ExternalLink className="ml-1 size-3" />
                </a>
              </Button>
            </div>
          </div>
        </CardHeader>
      }
    />
  );
}
